import { Box, Stack, Tooltip, Typography, styled } from "@mui/material";
import Image from "mui-image";
import Loading from "Components/Loading/Loading";
import { ClickableTooltip } from "Components/ClickableTooltip";
import { StyledHelp } from "Components/ClickableTooltip/StyledHelp";
import {
  getBadgeConfig,
  formatRevenueGrowthPercent,
  getPrimaryMetricLabel,
  getPrimaryMetricValue,
  getProductCategoryLabel,
  findProductByName,
  PRODUCT_CONVERSION_RATE_TOOLTIP,
} from "features/products/utils/productUtils";
import { TopPerformingProduct } from "features/products/types";
import ProductTrendSparkline from "features/products/components/ProductTrendSparkline";
import { useFormatMetric } from "features/analytics";
import { ApiMetric, ApiProduct } from "@incendium/api";
import { ProductCardLeadMetric } from "features/products/utils/productPerformanceTabs";
import { useMemo } from "react";

const CardRoot = styled(Box, {
  shouldForwardProp: (prop) => prop !== "fullWidth" && prop !== "interactive",
})<{ fullWidth?: boolean; interactive?: boolean }>(
  ({ theme, fullWidth, interactive }) => ({
    flex: fullWidth ? "1 1 auto" : "0 0 260px",
    width: fullWidth ? "100%" : 260,
    minHeight: fullWidth ? 0 : 340,
    borderRadius: theme.shape.borderRadius,
    border: `1px solid ${theme.palette.grey[200]}`,
    backgroundColor: theme.palette.common.white,
    padding: theme.spacing(2),
    display: "flex",
    flexDirection: "column",
    cursor: interactive ? "pointer" : "default",
    transition: "box-shadow 0.2s ease, transform 0.2s ease",
    ...(interactive
      ? {
          "&:hover": {
            boxShadow: "0 8px 24px rgba(94, 101, 229, 0.12)",
            transform: "translateY(-2px)",
          },
        }
      : {}),
  }),
);

const Badge = styled(Box)<{ badgecolor: string; badgebg: string }>(
  ({ badgecolor, badgebg }) => ({
    display: "inline-flex",
    alignSelf: "flex-start",
    padding: "4px 10px",
    borderRadius: 999,
    fontSize: 12,
    fontWeight: 600,
    color: badgecolor,
    backgroundColor: badgebg,
    marginBottom: 12,
  }),
);

const ImageWrap = styled(Box)(({ theme }) => ({
  width: "100%",
  height: 120,
  borderRadius: theme.shape.borderRadius,
  overflow: "hidden",
  backgroundColor: theme.palette.grey[50],
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  marginBottom: theme.spacing(1.5),
}));

const MetricValueRow = styled(Stack)({
  flexDirection: "row",
  alignItems: "center",
  gap: 4,
  justifyContent: "space-between",
});

const MetricLabel = styled(Typography)(({ theme }) => ({
  fontSize: 11,
  color: theme.palette.text.secondary,
  textTransform: "uppercase",
  letterSpacing: 0.4,
}));

const MetricValue = styled(Typography)({
  fontSize: 14,
  fontWeight: 700,
});

const SparklineWrap = styled(Box)({
  marginTop: "auto",
  height: 48,
  width: "100%",
});

interface ProductAnalyticsCardProps {
  product: TopPerformingProduct;
  products: ApiProduct[];
  trendMetric: ApiMetric;
  leadMetric?: ProductCardLeadMetric;
  loading?: boolean;
  fullWidth?: boolean;
  hideBadge?: boolean;
  onClick?: (productId?: number) => void;
}

function ProductAnalyticsCard({
  product,
  products,
  trendMetric,
  leadMetric = "primary",
  loading,
  fullWidth,
  hideBadge,
  onClick,
}: ProductAnalyticsCardProps) {
  const formatMetric = useFormatMetric();
  const apiProduct = useMemo(
    () => findProductByName(products, product.name),
    [products, product.name],
  );
  const badgeStyle = getBadgeConfig(product.badge.variant);
  const category = getProductCategoryLabel(apiProduct);

  const isGrowthLead = leadMetric === "revenue_growth";
  const isMarginLead =
    !isGrowthLead &&
    product.primaryMetric === ApiMetric.METRIC_AVERAGE_MARGIN_PERCENT;

  const leadLabel = isGrowthLead
    ? "Growth"
    : getPrimaryMetricLabel(product.primaryMetric);
  const leadValue = isGrowthLead
    ? formatRevenueGrowthPercent(product.revenueGrowthPercent ?? 0)
    : formatMetric(
        product.primaryMetric,
        getPrimaryMetricValue(product),
        product.primaryMetric === ApiMetric.METRIC_PRODUCT_REVENUE ||
          product.primaryMetric === ApiMetric.METRIC_PRODUCT_GATEWAY_LTV
          ? { isKM: true }
          : undefined,
      );

  const hasProductPageviews = product.pageviews > 0;
  const conversionDisplay = hasProductPageviews
    ? formatMetric(
        ApiMetric.METRIC_VISIT_TO_PURCHASE_RATE,
        product.conversionRate,
      )
    : "—";

  if (loading) {
    return (
      <CardRoot fullWidth={fullWidth} interactive={Boolean(onClick)}>
        <Loading />
      </CardRoot>
    );
  }

  return (
    <CardRoot
      fullWidth={fullWidth}
      interactive={Boolean(onClick)}
      onClick={onClick ? () => onClick(apiProduct?.id) : undefined}
    >
      {!hideBadge ? (
        <Tooltip
          title={badgeStyle.tooltip}
          arrow
          placement="top"
          slotProps={{
            tooltip: {
              sx: { maxWidth: 260, textAlign: "center" },
            },
          }}
        >
          <Badge badgecolor={badgeStyle.color} badgebg={badgeStyle.bgColor}>
            {product.badge.label}
          </Badge>
        </Tooltip>
      ) : null}

      <ImageWrap>
        {apiProduct?.imageUrl ? (
          <Image
            src={apiProduct.imageUrl}
            alt={product.name}
            height={120}
            width="100%"
            fit="contain"
            duration={0}
          />
        ) : (
          <Typography variant="body2" color="text.secondary">
            No image
          </Typography>
        )}
      </ImageWrap>

      <Typography fontWeight={700} fontSize={15} noWrap title={product.name}>
        {product.name}
      </Typography>
      <Typography variant="body2" color="text.secondary" mb={1.5} noWrap>
        {category}
      </Typography>

      <Stack direction="row" spacing={2} mb={1.5}>
        <Box flex={1}>
          <MetricLabel>{leadLabel}</MetricLabel>
          <MetricValue>{leadValue}</MetricValue>
        </Box>
        <Box flex={1}>
          <MetricLabel>{isMarginLead ? "Revenue" : "Margin"}</MetricLabel>
          <MetricValue>
            {isMarginLead
              ? formatMetric(
                  ApiMetric.METRIC_PRODUCT_REVENUE,
                  product.revenue,
                  {
                    isKM: true,
                  },
                )
              : product.marginPercent !== undefined
                ? formatMetric(
                    ApiMetric.METRIC_AVERAGE_MARGIN_PERCENT,
                    product.marginPercent,
                  )
                : "—"}
          </MetricValue>
        </Box>
        <Box flex={1}>
          <MetricLabel>Conversion</MetricLabel>
          <MetricValueRow>
            <MetricValue>{conversionDisplay}</MetricValue>
            <Box
              component="span"
              onClick={(event) => event.stopPropagation()}
              sx={{ display: "inline-flex", lineHeight: 0 }}
            >
              <ClickableTooltip
                text={PRODUCT_CONVERSION_RATE_TOOLTIP}
                placement="top"
                width={260}
                icon={<StyledHelp sx={{ fontSize: 14 }} />}
              />
            </Box>
          </MetricValueRow>
        </Box>
      </Stack>

      <SparklineWrap>
        <ProductTrendSparkline
          productName={product.name}
          trendMetric={trendMetric}
        />
      </SparklineWrap>
    </CardRoot>
  );
}

export default ProductAnalyticsCard;
